import { Divider, ListSubheader } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { FlexibleSelectTextField } from '@/components/atoms/FlexibleSelectTextField';
import { CryptoAccountDetailsItem } from '@/components/molecules/CryptoAccountDetailsItem';
import { FundingMethodMenuItem } from '@/components/molecules/FundingMethodMenuItem';
import { useTranslator } from '@/hooks/useTranslator';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType, FundingMethodItem, PaymentRailType } from '@/types/currency';
import { isZeroOrLess } from '@/utility/isZero';

interface Props {
  readonly label: string;
  readonly items: readonly FundingMethodItem[];
  readonly value: string;
  readonly balances: Partial<Record<CurrencyCodeType, DetailedBalance>>;

  readonly error?: boolean;
  readonly name?: string;
  readonly helperText?: string | null;

  onChange(value: FundingMethodItem): void;
}

export const FundingMethodSelect: React.FC<Props> = (props: Props): React.ReactElement => {
  const { label, items, value, name, balances, onChange, error, helperText } = props;

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: methodId } = event.target;

      const found = items.find((item: FundingMethodItem): boolean => item.id === methodId);
      if (!found) {
        console.debug(`Funding method with id ${methodId} not found`);
        return;
      }

      onChange(found);
    },
    [items, onChange],
  );

  const wallets = React.useMemo((): readonly FundingMethodItem[] => {
    return items.filter((item: FundingMethodItem): boolean => {
      return item.method === PaymentRailType.CalizaWallet;
    });
  }, [items]);

  const methods = React.useMemo((): readonly FundingMethodItem[] => {
    return items.filter(
      (item: FundingMethodItem): boolean => item.method !== PaymentRailType.CalizaWallet,
    );
  }, [items]);

  const tr = useTranslator();

  return (
    <FlexibleSelectTextField
      name={name}
      label={label}
      select={true}
      fullWidth={true}
      value={value}
      error={error}
      helperText={helperText}
      onChange={handleChange}>
      {wallets.length > 0 && <ListSubheader>{tr('USE_YOUR_CALIZA_BALANCE')}</ListSubheader>}
      {wallets.map((fundingMethod: FundingMethodItem): React.ReactElement => {
        const { currency } = fundingMethod;

        const balance = balances[currency] ?? DetailedBalance.zero(currency);
        const outOfFunds = isZeroOrLess(balance.available);

        return (
          <MenuItem key={fundingMethod.id} value={fundingMethod.id} disabled={outOfFunds}>
            <CryptoAccountDetailsItem
              balanceAmount={balance.available}
              currency={currency}
              available={!outOfFunds}
            />
          </MenuItem>
        );
      })}

      {methods.length > 0 && wallets.length > 0 && <Divider />}
      {methods.length > 0 && <ListSubheader sx={styles.spacer} />}
      {methods.length > 0 && <ListSubheader>{tr('SEND_FUNDS_TO_COMPLETE_TRANSFER')}</ListSubheader>}
      {methods.map((fundingMethod: FundingMethodItem): React.ReactElement => {
        return (
          <MenuItem key={fundingMethod.id} value={fundingMethod.id}>
            <FundingMethodMenuItem fundingMethod={fundingMethod} />
          </MenuItem>
        );
      })}
    </FlexibleSelectTextField>
  );
};

const styles: Record<string, SystemStyleObject> = {
  spacer: {
    pt: 0,
  },
};
